import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, FileText } from "lucide-react";

export default function IntroductoryReport({ applicationId, canEdit = false }) {
  const [form, setForm] = useState({ student_expectations: "", training_objectives: "", mentor_notes: "" });
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/introductory-reports/${applicationId}`);
      setExisting(data.report);
      setForm({
        student_expectations: data.report?.student_expectations || "",
        training_objectives: data.report?.training_objectives || "",
        mentor_notes: data.report?.mentor_notes || "",
      });
    } catch { /* no report yet is fine */ }
    finally { setLoading(false); }
  }, [applicationId]);
  useEffect(() => { if (applicationId) load(); }, [applicationId, load]);

  const save = async () => {
    if (!form.student_expectations.trim() || !form.training_objectives.trim()) {
      toast.error("توقّعات الطالب وأهداف التدريب مطلوبة");
      return;
    }
    setSaving(true);
    try {
      await api.post("/introductory-reports", { application_id: applicationId, ...form });
      toast.success("تم حفظ تقرير اللقاء التعريفي");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="rounded-2xl bg-white border border-slate-200 p-6 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-teal-700" /></div>;

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="intro-report">
      <div className="flex items-center gap-2 mb-4">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-indigo-50 text-indigo-700"><FileText className="h-5 w-5" /></div>
        <div>
          <h3 className="font-heading font-black text-slate-900">تقرير اللقاء التعريفي</h3>
          <div className="text-xs text-slate-500">يُعبّأ بعد أول جلسة بين المرشد والطالب.</div>
        </div>
      </div>
      {!canEdit && !existing ? (
        <div className="rounded-xl bg-slate-50 py-8 text-center text-sm text-slate-500">لم يُسجّل التقرير بعد.</div>
      ) : (
        <div className="space-y-4">
          <Field label="توقّع الطالب من التدريب" testid="intro-expectations">
            <textarea rows={3} value={form.student_expectations} disabled={!canEdit} onChange={(e) => setForm({ ...form, student_expectations: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 disabled:bg-slate-50 disabled:text-slate-700" data-testid="intro-expectations-input" />
          </Field>
          <Field label="أهداف التدريب" testid="intro-objectives">
            <textarea rows={3} value={form.training_objectives} disabled={!canEdit} onChange={(e) => setForm({ ...form, training_objectives: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 disabled:bg-slate-50 disabled:text-slate-700" data-testid="intro-objectives-input" />
          </Field>
          <Field label="ملاحظات المرشد (اختياري)" testid="intro-notes">
            <textarea rows={3} value={form.mentor_notes} disabled={!canEdit} onChange={(e) => setForm({ ...form, mentor_notes: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 disabled:bg-slate-50 disabled:text-slate-700" data-testid="intro-notes-input" />
          </Field>
          {canEdit && (
            <div className="flex justify-end">
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 min-h-11 px-5 rounded-xl bg-teal-700 text-white font-bold hover:bg-teal-600 disabled:opacity-60" data-testid="intro-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} حفظ التقرير
              </button>
            </div>
          )}
          {existing?.updated_at && (
            <div className="text-xs text-slate-400">آخر تحديث: {new Date(existing.updated_at).toLocaleString("ar-SA")}</div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="block text-xs font-semibold text-slate-700 mb-1">{label}</div>
      {children}
    </div>
  );
}
